import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { handlePayment } from "@/services/event";
import { BookEventInput, Event } from "@/types/event";
import { makeEventDetailsQueryKey } from "@/lib/reactQuery";
import { wait } from "@/lib/utils";
import { getCreditBookingAttributionArgs } from "@/lib/lastTouchAttribution";

export const useEventBooking = (specialOfferCode?: string | null, urlPartnerCode?: string | null) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookedSeats, setBookedSeats] = useState(0);
  const queryClient = useQueryClient();

  const bookingMutation = useMutation({
    mutationFn: async ({
      event,
      seats,
      referralCode,
      groupId,
    }: {
      event: Event | null;
      seats: number;
      useCredits: boolean;
      referralCode: string | undefined;
      groupId?: string | null;
      forFriend?: boolean;
    }) => {
      
      const { data: ticketId, error } = await supabase.rpc("book_event_by_credits", {
        event_id: event.id,
        quantity: seats,
        _referral_code: referralCode || null,
        _group_id: groupId || null,
        ...getCreditBookingAttributionArgs(),
      });

      if (error) {
        console.error("Ticket creation error:", error);
        throw new Error("Error creating ticket");
      }

      // send email confirmation
      supabase.functions.invoke("send-ticket-confirmation", {
        body: { ticketId },
      });

      return ticketId;
    },
    onSuccess: (ticketId, variables) => {
      setBookedSeats(variables.seats);
      const forFriendParam = variables.forFriend ? '&forFriend=true' : '';
      navigate(`/thank-you?type=ticket&event_id=${variables.event?.id}&seats=${variables.seats}&method=credits&amount=0&currency=gbp&booking_ref=${ticketId}${forFriendParam}`);
    },
    onError: (error) => {
      console.error("Booking error:", error);
      toast({
        title: "Booking Failed",
        description:
          "There was an error confirming your booking. Please try again or contact support.",
        variant: "destructive",
      });
      throw error; // so error tracking can catch it
    },
  });

  const bookByCredits = useCallback(
    async (event: Event, seats: number, referralCode: string | undefined, forFriend?: boolean, groupId?: string | null) => {
      bookingMutation.mutate({
        seats,
        useCredits: true,
        event,
        referralCode,
        forFriend,
        groupId,
      });
    },
    [bookingMutation]
  );

  const bookByStripe = useCallback(
    async (event: Event, seats: number, referralCode: string | undefined, applyCredits: boolean = true, forFriend?: boolean, joinMembership?: boolean, groupId?: string | null) => {
      // Redirect to Stripe for payment
      handlePayment({
        event,
        seats,
        onSuccess: () => {
          // Success is handled by URL parameters after Stripe redirect
        },
        onError: (message?: string, opts?: { status?: number }) => {
          // 409 = capacity refusal from create-checkout: nobody was charged, so
          // "Payment Failed" would be wrong — it's a stock question, not a payment one.
          const title = opts?.status === 409 ? "Not enough seats" : "Payment Failed";
          toast({
            title,
            description: message || "Please try again or use a different payment method.",
            variant: "destructive",
          });
          if (opts?.status === 409) {
            queryClient.invalidateQueries({ queryKey: ["event-group-availability"] });
          }
        },
        referralCode,
        specialOfferCode,
        applyCredits,
        urlPartnerCode,
        forFriend,
        joinMembership,
        groupId,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, specialOfferCode]
  );

  const bookByUnlimited = useCallback(
    async (event: Event, seats: number, groupId?: string | null) => {
      const { data: ticketId, error } = await supabase.rpc("book_event_unlimited", {
        event_id: event.id,
        quantity: seats,
        _group_id: groupId || null,
        ...getCreditBookingAttributionArgs(),
      });

      if (error) {
        console.error("Unlimited booking error:", error);
        toast({
          title: "Booking Failed",
          description: error.message || "There was an error confirming your booking. Please try again.",
          variant: "destructive",
        });
        throw error;
      }

      // send email confirmation
      supabase.functions.invoke("send-ticket-confirmation", {
        body: { ticketId },
      });

      setBookedSeats(seats);
      navigate(`/thank-you?type=ticket&event_id=${event.id}&seats=${seats}&method=unlimited&amount=0&currency=gbp&booking_ref=${ticketId}`);
    },
    [toast, navigate]
  );

  const handleBook = useCallback(
    async (input: BookEventInput) => {
      const { event, seats, useCredits, referralCode, useUnlimited, applyCredits = true, forFriend, joinMembership, groupId } = input;
      
      // Check for unlimited booking first (skip if booking for friend)
      if (useUnlimited && !forFriend) {
        await bookByUnlimited(event, seats, groupId);
        await wait(1000);
        queryClient.invalidateQueries({
          queryKey: makeEventDetailsQueryKey(event.id),
        });
        return;
      }
      
      // Only use the credit RPC when credits are actually being spent.
      // Free or fully-discounted (£0) bookings still go through Stripe so partner codes are recorded.
      if (useCredits) {
        await bookByCredits(event, seats, referralCode, forFriend, groupId);
        await wait(1000);
        queryClient.invalidateQueries({
          queryKey: makeEventDetailsQueryKey(event.id),
        });
        return;
      } 

      // Otherwise, book via Stripe (with optional credit application)
      await bookByStripe(event, seats, referralCode, applyCredits, forFriend, joinMembership, groupId);
      return;
    },
    [bookByCredits, bookByStripe, bookByUnlimited, queryClient]
  );

  return {
    showConfirmation,
    setShowConfirmation,
    bookedSeats,
    handleBook,
    bookingMutation,
    setBookedSeats,
    isProcessing: bookingMutation.isPending
  };
};